import type { Assistant } from '@/types/assistant'
import type { OutcomeType } from '@/types/conversation'

export type InboxChannel = 'phone' | 'whatsapp'

export interface InboxFilters {
  channels: InboxChannel[]
  assistant_ids: number[]
  outcomes: OutcomeType[]
  customer_ids: number[]
  booking_systems: string[]
  backend_versions: string[]
  tags: string[]
  /** Substring match on the classified topic */
  topic: string
  /** Substring match on the summary */
  summary: string
  /** Substring match on the caller number (`from_id`) */
  phone_number: string
  /** Inclusive `created_at` range as `YYYY-MM-DD`, empty when open-ended */
  date_from: string
  date_to: string
}

export const INBOX_CHANNELS: InboxChannel[] = ['phone', 'whatsapp']

export const INBOX_OUTCOMES: OutcomeType[] = [
  'completed',
  'caller_follow_up',
  'business_follow_up',
  'transferred',
  'information_provided',
  'no_action',
]

/** Booking system tool names as they appear in an assistant's `config.tools` */
export const BOOKING_SYSTEMS = [
  { value: 'formitable', label: 'Formitable' },
  { value: 'opentable', label: 'OpenTable' },
  { value: 'fork', label: 'TheFork' },
  { value: 'guestplan', label: 'Guestplan' },
  { value: 'zenchef', label: 'Zenchef' },
  { value: 'gotable', label: 'GoTable' },
  { value: 'tablefever', label: 'TableFever' },
]

/** Tag changes wait this long so several checks become one list request. */
export const INBOX_TAG_SETTLE_MS = 300

export function inboxTagsChanged(current: InboxFilters, next: InboxFilters): boolean {
  if (current.tags.length !== next.tags.length) return true
  return current.tags.some((tag, index) => tag !== next.tags[index])
}

export const EMPTY_INBOX_FILTERS: InboxFilters = {
  channels: [],
  assistant_ids: [],
  outcomes: [],
  customer_ids: [],
  booking_systems: [],
  backend_versions: [],
  tags: [],
  topic: '',
  summary: '',
  phone_number: '',
  date_from: '',
  date_to: '',
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

function isoDate(value: unknown): string {
  return typeof value === 'string' && ISO_DATE.test(value) ? value : ''
}

function numberArray(value: unknown): number[] {
  return Array.isArray(value)
    ? value.filter((item): item is number => typeof item === 'number')
    : []
}

/** Channel and backend version each take one value. A longer list is dropped. */
function oneChannel(value: unknown): InboxChannel[] {
  const channels = stringArray(value).filter((item): item is InboxChannel =>
    INBOX_CHANNELS.includes(item as InboxChannel)
  )
  return channels.length === 1 ? channels : []
}

function knownOutcomes(value: unknown): OutcomeType[] {
  return stringArray(value).filter((item): item is OutcomeType =>
    INBOX_OUTCOMES.includes(item as OutcomeType)
  )
}

function oneVersion(value: unknown): string[] {
  const versions = stringArray(value)
    .map((item) => item.trim())
    .filter(Boolean)
  return versions.length === 1 ? versions : []
}

/** Reads a `?filter=` value. Invalid JSON yields empty filters so a bad link still opens the inbox. */
export function parseInboxFilters(value: string | null): InboxFilters {
  if (!value) return EMPTY_INBOX_FILTERS
  try {
    const parsed: unknown = JSON.parse(value)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return EMPTY_INBOX_FILTERS
    const record = parsed as Record<string, unknown>
    return {
      channels: oneChannel(record.channels),
      assistant_ids: numberArray(record.assistant_ids),
      outcomes: knownOutcomes(record.outcomes),
      customer_ids: numberArray(record.customer_ids),
      booking_systems: stringArray(record.booking_systems),
      backend_versions: oneVersion(record.backend_versions),
      tags: stringArray(record.tags),
      topic: text(record.topic),
      summary: text(record.summary),
      phone_number: text(record.phone_number),
      date_from: isoDate(record.date_from),
      date_to: isoDate(record.date_to),
    }
  } catch {
    return EMPTY_INBOX_FILTERS
  }
}

/** Compact JSON for the `filter` query param, or null when nothing is selected. */
export function serializeInboxFilters(filters: InboxFilters): string | null {
  const cleaned: Record<string, unknown> = {}
  if (filters.channels.length === 1) cleaned.channels = filters.channels
  if (filters.assistant_ids.length) cleaned.assistant_ids = filters.assistant_ids
  if (filters.outcomes.length) cleaned.outcomes = filters.outcomes
  if (filters.customer_ids.length) cleaned.customer_ids = filters.customer_ids
  if (filters.booking_systems.length) cleaned.booking_systems = filters.booking_systems
  if (filters.backend_versions.length === 1) cleaned.backend_versions = filters.backend_versions
  if (filters.tags.length) cleaned.tags = filters.tags
  if (filters.topic) cleaned.topic = filters.topic
  if (filters.summary) cleaned.summary = filters.summary
  if (filters.phone_number) cleaned.phone_number = filters.phone_number
  if (filters.date_from) cleaned.date_from = filters.date_from
  if (filters.date_to) cleaned.date_to = filters.date_to
  return Object.keys(cleaned).length ? JSON.stringify(cleaned) : null
}

function channelFilter(channels: InboxChannel[]): Record<string, string> {
  return channels.length === 1 ? { channel: channels[0] } : {}
}

function usesBookingSystem(
  assistant: Pick<Assistant, 'config'>,
  bookingSystems: string[]
): boolean {
  const tools = assistant.config?.tools
  return (
    Array.isArray(tools) &&
    tools.some(
      (tool: Record<string, unknown>) =>
        typeof tool?.name === 'string' && bookingSystems.includes(tool.name)
    )
  )
}

/**
 * The list endpoint cannot filter on assistant config, so the booking system selection
 * is resolved to the assistants using it, narrowed by any assistant selection.
 */
function assistantIdFilter(
  filters: InboxFilters,
  assistants: Pick<Assistant, 'id' | 'config'>[]
): number[] | undefined {
  if (!filters.booking_systems.length) {
    return filters.assistant_ids.length ? filters.assistant_ids : undefined
  }

  const ids = assistants
    .filter((assistant) => usesBookingSystem(assistant, filters.booking_systems))
    .map((assistant) => assistant.id)
    .filter((id) => !filters.assistant_ids.length || filters.assistant_ids.includes(id))

  // No assistant matches: keep the filter so the list comes back empty instead of unfiltered
  return ids.length ? ids : [-1]
}

/**
 * Builds the react-admin filter for the inbox list.
 *
 * The list endpoint ANDs top-level keys. Topic and summary are wrapped in `%` the same
 * way the previous conversation list did. Channel and backend version are a single value
 * each; an empty channel means every channel. Several outcomes match any one of them.
 */
export function buildInboxFilter(
  filters: InboxFilters,
  assistants: Pick<Assistant, 'id' | 'config'>[] = []
): Record<string, unknown> {
  const filter: Record<string, unknown> = { ...channelFilter(filters.channels) }

  const assistantIds = assistantIdFilter(filters, assistants)
  if (assistantIds) {
    filter.assistant_id = assistantIds
  }

  if (filters.customer_ids.length) {
    // Nested dicts make the backend join the related model
    filter.assistant = { customer_id: filters.customer_ids }
  }

  // The previous list wrapped these two itself. The value is sent as typed.
  if (filters.topic) {
    filter.topic = `%${filters.topic}%`
  }

  if (filters.summary) {
    filter.summary = `%${filters.summary}%`
  }

  if (filters.phone_number) {
    filter.from_id = filters.phone_number
  }

  // A list of strings is a JSON contains check, ORed across the selected tags
  if (filters.tags.length) {
    filter.tags = filters.tags
  }

  // The list endpoint bounds `created_at` by these dates, both days inclusive
  if (filters.date_from) {
    filter.date_from = filters.date_from
  }
  if (filters.date_to) {
    filter.date_to = filters.date_to
  }

  if (filters.outcomes.length === 1) {
    filter.outcome = filters.outcomes[0]
  } else if (filters.outcomes.length > 1) {
    // A list of strings would become a JSON contains check, which a plain text column fails
    filter._or = filters.outcomes.map((outcome) => ({ outcome }))
  }

  if (filters.backend_versions.length === 1) {
    filter.backend_api_version = filters.backend_versions[0]
  }

  return filter
}
